import ClusterErrorCard from "./ClusterErrorCard"
import PointDetailCard from "./PointDetailCard"
import type { ParasiteRatePoint, ValidationFailurePoint } from "@/services/parasiteRateService"

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface ClusterDetailPanelProps {
  /** The selected data point to display details for. */
  point: ParasiteRatePoint
  /** Callback to close/dismiss the panel. */
  onClose: () => void
  /**
   * Whether the map is in validation error mode.
   */
  errorModeActive: boolean
  /**
   * Total number of points sharing the selected point's map location (including
   * the selected point itself). 1 when the point is not stacked with others.
   */
  coLocatedCount: number
  /**
   * Zero-based index of the selected point within the co-located group, used to
   * render the "n / total" position indicator.
   */
  coLocatedIndex: number
  /** Select the previous point in the co-located group (wraps around). */
  onNavigatePrev: () => void
  /** Select the next point in the co-located group (wraps around). */
  onNavigateNext: () => void
  /** The full set of validation failure points currently loaded.*/
  allValidationFailures: ValidationFailurePoint[]
}

/**
 * ClusterDetailPanel is the right-side overlay panel for a selected map point.
 * It owns the panel positioning and overall height, and composes two children:
 *
 * @param props - The selected point, close handler, and whether error mode is
 *   active.
 * @returns A positioned panel overlaid on the right side of the map.
 */
export default function ClusterDetailPanel({
  point,
  onClose,
  errorModeActive,
  coLocatedCount,
  coLocatedIndex,
  onNavigatePrev,
  onNavigateNext,
  allValidationFailures,
}: ClusterDetailPanelProps): React.JSX.Element {
  return (
    <div className="absolute top-26 right-4 flex flex-col items-end gap-2 z-30 max-h-[calc(100%-8rem)] md:top-4 md:max-h-[calc(100%-2rem)]">
      {errorModeActive && (
        <div className="shrink-0">
          <ClusterErrorCard
            point={point as ValidationFailurePoint}
            allValidationFailures={allValidationFailures}
          />
        </div>
      )}
      <PointDetailCard
        point={point}
        onClose={onClose}
        coLocatedCount={coLocatedCount}
        coLocatedIndex={coLocatedIndex}
        onNavigatePrev={onNavigatePrev}
        onNavigateNext={onNavigateNext}
      />
    </div>
  )
}
